// src/pages/AttendanceManagePage.jsx
// re-push trigger
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
  TextField, Snackbar, Stack, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, useTheme, useMediaQuery
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { getTeacherLessons } from '../api/reportApi';
import { getLessonStudents, getAttendance, saveAttendance, getAttendanceNote, saveAttendanceNote } from '../api/attendanceApi';
import { gregorianToJalaaliString, jalaaliStringToGregorianIso } from '../utils/jalaaliHelper';

export default function AttendanceManagePage() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [lessons, setLessons] = useState([]);
  const [lessonId, setLessonId] = useState('');
  const [dateIso, setDateIso] = useState(new Date().toISOString().slice(0, 10));
  const [jalaaliDate, setJalaaliDate] = useState(gregorianToJalaaliString(new Date().toISOString().slice(0, 10)));
  const [sessionIndex, setSessionIndex] = useState(1);
  const [students, setStudents] = useState([]);
  const [teacherNote, setTeacherNote] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '' });

  useEffect(() => {
    (async () => {
      try {
        const arr = await getTeacherLessons();
        setLessons(Array.isArray(arr) ? arr : []);
      } catch (e) {
        console.error('load lessons failed', e);
        setLessons([]);
      }
    })();
  }, []);

  const loadData = async () => {
    if (!lessonId) return setSnack({ open: true, msg: 'درس را انتخاب کنید' });
    try {
      const studs = await getLessonStudents(lessonId);
      const normalized = (studs || []).map(s => ({
        id: s.Id ?? s.id,
        name: s.Name ?? s.name ?? `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim(),
        code: s.Code ?? s.code ?? s.StudentCode ?? ''
      }));

      const att = await getAttendance(Number(lessonId), dateIso, sessionIndex);
      const attMap = (att?.items || []).reduce((acc, it) => { acc[it.studentId] = it; return acc; }, {});

      const merged = normalized.map(s => ({
        studentId: s.id,
        studentName: s.name,
        studentCode: s.code,
        status: attMap[s.id]?.status || 'present',
        note: attMap[s.id]?.note || ''
      }));
      setStudents(merged);

      const noteRes = await getAttendanceNote(Number(lessonId), dateIso, sessionIndex);
      setTeacherNote(noteRes?.note || '');
    } catch (err) {
      console.error(err);
      setSnack({ open: true, msg: 'خطا در بارگذاری' });
    }
  };

  const handleSave = async () => {
    if (!lessonId) return setSnack({ open: true, msg: 'درس را انتخاب کنید' });
    const payload = {
      lessonId: Number(lessonId),
      date: dateIso,
      sessionIndex,
      items: students.map(s => ({
        studentId: s.studentId,
        status: s.status,
        note: s.note
      }))
    };
    try {
      await saveAttendance(payload);
      await saveAttendanceNote({ lessonId: Number(lessonId), date: dateIso, sessionIndex, note: teacherNote });
      setSnack({ open: true, msg: 'حضور و یادداشت ذخیره شد' });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, msg: 'خطا در ذخیره' });
    }
  };

  const stats = students.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
  const getRowStyle = (status) => {
    switch (status) {
      case 'present': return { backgroundColor: 'rgba(76,175,80,0.06)' };
      case 'absent': return { backgroundColor: 'rgba(244,67,54,0.08)' };
      case 'excused': return { backgroundColor: 'rgba(33,150,243,0.06)' };
      case 'late': return { backgroundColor: 'rgba(255,152,0,0.06)' };
      default: return {};
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1100, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel id="lesson-select-label">درس</InputLabel>
            <Select
              labelId="lesson-select-label"
              value={lessonId}
              label="درس"
              onChange={e => setLessonId(e.target.value)}
            >
              <MenuItem value="">انتخاب کنید</MenuItem>
              {lessons.map(l => (
                <MenuItem key={l.id ?? l.Id} value={l.id ?? l.Id}>
                  {l.title ?? l.Title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="تاریخ هجری"
            value={jalaaliDate}
            onChange={e => {
              setJalaaliDate(e.target.value);
              const iso = jalaaliStringToGregorianIso(e.target.value);
              if (iso) setDateIso(iso);
            }}
            sx={{ width: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            size="small"
          />

          <FormControl sx={{ width: { xs: '100%', sm: 120 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel>جلسه</InputLabel>
            <Select value={sessionIndex} onChange={e => setSessionIndex(Number(e.target.value))} size="small">
              <MenuItem value={1}>جلسه 1</MenuItem>
              <MenuItem value={2}>جلسه 2</MenuItem>
              <MenuItem value={3}>جلسه 3</MenuItem>
              <MenuItem value={4}>جلسه 4</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
            <Button variant="contained" onClick={loadData} startIcon={<EditIcon />} size="small">بارگذاری</Button>
            <Button variant="contained" color="success" onClick={handleSave} startIcon={<SaveIcon />} size="small">ذخیره تغییرات</Button>
          </Stack>
        </Stack>
      </Paper>

      {students.length > 0 && (
        <Paper sx={{ p: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`حاضر: ${stats.present || 0}`} color="success" />
          <Chip label={`غایب: ${stats.absent || 0}`} color="error" />
          <Chip label={`موجه: ${stats.excused || 0}`} color="info" />
          <Chip label={`تاخیر: ${stats.late || 0}`} color="warning" />
          <Chip label={`کل: ${students.length}`} variant="outlined" />
        </Paper>
      )}

      {students.length > 0 && isSmUp && (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>نام دانش‌آموز</TableCell>
                <TableCell>کد</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>توضیح</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map(s => (
                <TableRow key={s.studentId} sx={getRowStyle(s.status)}>
                  <TableCell sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{s.studentName}</TableCell>
                  <TableCell>#{s.studentCode}</TableCell>
                  <TableCell>
                    <FormControl sx={{ minWidth: 120 }} size="small">
                      <Select
                        value={s.status}
                        onChange={e => setStudents(prev => prev.map(x => x.studentId === s.studentId ? { ...x, status: e.target.value } : x))}
                        size="small"
                      >
                        <MenuItem value="present">حاضر</MenuItem>
                        <MenuItem value="absent">غایب</MenuItem>
                        <MenuItem value="excused">موجه</MenuItem>
                        <MenuItem value="late">تاخیر</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="توضیح"
                      value={s.note || ''}
                      onChange={e => setStudents(prev => prev.map(x => x.studentId === s.studentId ? { ...x, note: e.target.value } : x))}
                      fullWidth
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {students.length > 0 && !isSmUp && (
        <Box>
          {students.map(s => (
            <Paper key={s.studentId} sx={{ p: 1, mb: 1, ...getRowStyle(s.status) }}>
              <Stack spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{s.studentName}</Typography>
                  <Typography variant="caption" color="text.secondary">#{s.studentCode}</Typography>
                </Box>

                <FormControl sx={{ width: '100%' }} size="small">
                  <InputLabel id={`status-label-${s.studentId}`}>وضعیت</InputLabel>
                  <Select
                    labelId={`status-label-${s.studentId}`}
                    value={s.status}
                    label="وضعیت"
                    onChange={e => setStudents(prev => prev.map(x => x.studentId === s.studentId ? { ...x, status: e.target.value } : x))}
                    size="small"
                  >
                    <MenuItem value="present">حاضر</MenuItem>
                    <MenuItem value="absent">غایب</MenuItem>
                    <MenuItem value="excused">موجه</MenuItem>
                    <MenuItem value="late">تاخیر</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  placeholder="توضیح"
                  value={s.note || ''}
                  onChange={e => setStudents(prev => prev.map(x => x.studentId === s.studentId ? { ...x, note: e.target.value } : x))}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>یادداشت دبیر برای این جلسه</Typography>
        <TextField
          label="یادداشت"
          placeholder="کارهایی که انجام شد و برنامهٔ جلسهٔ بعد..."
          multiline
          minRows={3}
          fullWidth
          value={teacherNote}
          onChange={e => setTeacherNote(e.target.value)}
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}